/* eslint-disable no-underscore-dangle */

/**
 * Create a modal overlay.
 *
 * The modal will make sure that tab focus is kept within itself. Focus will be set
 * back to the element that was focused when the modal was opened.
 *
 * @param {String} title The title of the modal overlay. Not visible, but necessary
 *        for screen readers.
 * @param {Node} content The contents of the modal overlay
 * @param {Object} options The options object can be used to specify these parameters:
 *        `onDismiss`:
 *            a function to call when `dismiss()` is called. It's passed the same
 *            arguments as the `dismiss()` method.
 *        `size`:
 *            an object which can specify `top`, `bottom`, `left`, `right`, `maxWidth`
 *            and `maxHeight`, set as the corresponding CSS properties of the modal container.
 *            If no size is specified, `top`, `bottom`, `left` and `right` are set to `0`.
 *        `onHideFocusElement`:
 *            The element to focus when the modal gets hidden(). If this is not specified,
 *            the element that was focused when the modal was opened has its focus set back.
 */
class ModalOverlay {
    constructor(title, content, options = {}, shadowRoot) {
        if (!title) {
            throw Error('A title has to be provided');
        }

        this._title = title;
        this._content = content;
        this._options = options;
        this._documentRoot = shadowRoot || document;

        this._dismissCallback = null;
        if (options.onDismiss) {
            this._dismissCallback = options.onDismiss.bind(this);
        }
        this._onHideFocusElement = options.onHideFocusElement || null;
        this._lastFocusedElement = null;

        this._handleWindowFocusin = this._handleWindowFocusin.bind(this);
        this._handleWindowBlur = this._handleWindowBlur.bind(this);
        this._dismissIfClickedOutside =
            this._dismissIfClickedOutside.bind(this);
        this._handleKeydown = this._handleKeydown.bind(this);
        this._animationDurationInMillis = 300;
    }

    /**
     * Show the modal if another modal is not alreay open
     */
    show() {
        if (ModalOverlay.isOpen) {
            return;
        }
        ModalOverlay.isOpen = true;
        this._onHideFocusElement =
            this._options.onHideFocusElement ||
            this._documentRoot.activeElement;
        this._createElements();
        this._setPosition();
        // window.addEventListener('focusin', this._handleWindowFocusin);
        // window.addEventListener('blur', this._handleWindowBlur);
        // Add this in a timeout so that any click handler that opens the modal don't have to call
        // stopPropagation() (forgetting that leads to confusing bugs where the modal don't show up)
        window.setTimeout(() =>
            document.addEventListener('click', this._dismissIfClickedOutside),
        );
        document.addEventListener('keydown', this._handleKeydown);

        // iOS bugfix for animating the show and hide dialog.
        // Problem:  cannot set focus and then transform the object.
        //           Therefore the focus has to be set first, then the object can be moved.
        // The hack: set opacity zero, set focus, then do the transformation.
        const { style } = this._containerEle;
        if (this._options.slideUp) {
            style.opacity = '0';
        }

        this._documentRoot.appendChild(this._backdropEle);
        this._focusElement();

        if (this._options.slideUp) {
            // Use requestAnimationFrame to avoid running this at the same time as the focus()
            // for smoother animation.
            window.requestAnimationFrame(() => {
                style.transform = 'translateY(100vh)';
                style.opacity = '';

                window.requestAnimationFrame(() => {
                    style.transition = `transform ${this._animationDurationInMillis}ms`;
                    style.transform = 'translateY(0)';
                });
            });
        }
    }

    /**
     * Hide the modal
     */
    hide() {
        const { style } = this._containerEle;
        if (this._options.slideDown) {
            style.transition = `transform ${this._animationDurationInMillis}ms`;
            window.requestAnimationFrame(() => {
                style.transform = 'translateY(100vh)';
                // + 50 millis to give the animation enough time to finish
                this._teardownAsync(this._animationDurationInMillis + 50);
            });
        } else {
            this._teardownAsync(0);
        }
    }

    _teardownAsync(duration) {
        setTimeout(() => {
            this._backdropEle.remove();
            window.removeEventListener('focusin', this._handleWindowFocusin);
            window.removeEventListener('blur', this._handleWindowBlur);
            document.removeEventListener(
                'click',
                this._dismissIfClickedOutside,
            );
            document.removeEventListener('keydown', this._handleKeydown);
            this._handleFocus(this._onHideFocusElement || document.body);
            this._onHideFocusElement = null;
            this._lastFocusedElement = null;
            ModalOverlay.isOpen = false;
        }, duration);
    }

    /**
     * Hide the modal. If an `onDismiss` callback has been specified, it's called with
     * the `reason` parameter.
     *
     * @param {String} reason The reason for why the modal was dismissed. This can be
     *        e.g. "ok" or "cancel" for a confirm dialog subclass.
     */
    dismiss(reason) {
        if (this._dismissCallback) {
            const noHide = this._dismissCallback(reason);
            if (noHide) {
                return;
            }
        }

        this.hide();
    }

    _handleFocus(element) {
        if (element.focus) {
            element.focus();
        }
    }

    _createElements() {
        this._backdropEle = document.createElement('div');
        this._backdropEle.className =
            'modal modal--is-active modal--is-fullscreen';
        this._backdropEle.addEventListener('touchstart', (evt) =>
            this._preventTouchOutside(evt),
        );
        this._containerEle = document.createElement('div');
        this._containerEle.className = 'modal__container';
        this._containerEle.tabIndex = '-1';
        this._containerEle.style.outline = 'none';
        this._containerEle.setAttribute('role', 'dialog');
        this._containerEle.setAttribute('aria-label', this._title);
        this._containerEle.appendChild(this._buildContent());
        this._backdropEle.appendChild(this._containerEle);
    }

    _buildContent() {
        const ele = document.createElement('div');
        ele.className = 'modal__content';
        ele.appendChild(this._content);
        return ele;
    }

    _setPosition() {
        const { size } = this._options;
        const { style } = this._containerEle;
        style.cssText = '';
        style.outline = 'none'; // FIXME: don't apply here

        if (size) {
            style.top = size.top || '0';
            style.bottom = size.bottom || '0';
            style.left = size.left || '0';
            style.right = size.right || '0';
            style.maxWidth = size.maxWidth || '100%';
            style.maxHeight = size.maxHeight || '100%';
            style.height = size.height || 'auto';
            style.width = size.width || 'auto';
        } else {
            style.top = '0';
            style.right = '0';
            style.bottom = '0';
            style.left = '0';
        }
    }

    _dismissIfClickedOutside(evt) {
        if (!this._documentRoot === evt.target) {
            this.dismiss('cancel');
            evt.stopPropagation();
        }
    }

    _preventTouchOutside(evt) {
        if (!this._containerEle.contains(evt.target)) {
            evt.preventDefault();
        }
    }

    _focusElement() {
        const autofocusEle =
            this._containerEle.querySelector('[autofocus]') ||
            this._containerEle.querySelector('[data-autofocus]') ||
            this._getFocusableElements(this._containerEle)[0] ||
            this._containerEle;
        autofocusEle.focus();
    }

    _getFocusableElements(element) {
        const selector = `a, audio[controls], button, details, embed, iframe, img[usemap],
            input:not([type="hidden"]), keygen, label, object[usemap], select,
            textarea, video[controls], [tabindex]`;
        return [...element.querySelectorAll(selector)]
            .filter((ele) => ele.tabIndex > -1) // Only include elements that have a positive tabindex
            .filter((ele) => !ele.matches("[aria-hidden='true'] *"));
    }

    _handleTabbing(shiftKey) {
        const focusableEles = this._getFocusableElements(this._containerEle);
        const firstFocusableEle = focusableEles[0];
        const lastFocusableEle = focusableEles[focusableEles.length - 1];
        if (
            this._documentRoot.activeElement === firstFocusableEle &&
            shiftKey
        ) {
            lastFocusableEle.focus();
            return true;
        }
        if (
            this._documentRoot.activeElement === lastFocusableEle &&
            !shiftKey
        ) {
            firstFocusableEle.focus();
            return true;
        }
        return false;
    }

    _handleKeydown(evt) {
        let isHandled = false;

        if (evt.keyCode === 27 /* Escape */) {
            isHandled = true;
            this.dismiss('cancel');
        } else if (evt.keyCode === 9 /* Tab */) {
            isHandled = this._handleTabbing(evt.shiftKey);
        }

        if (isHandled) {
            evt.stopPropagation();
            evt.preventDefault();
        }

        return isHandled;
    }

    /**
     * Handle the case where we have an open modal and the browser tab has been blurred
     * and then focused again
     */
    _handleWindowFocusin(evt) {
        if (!this._containerEle.contains(evt.target)) {
            if (this._lastFocusedElement) {
                this._lastFocusedElement.focus();
            } else {
                this._focusElement();
            }
            evt.stopPropagation();
        }
    }

    /**
     * Save the currently focused element when the browser tab is blurred. It's set back
     * in _handleWindowFocusin
     */
    _handleWindowBlur() {
        this._lastFocusedElement = this._documentRoot.activeElement;
    }
}

ModalOverlay.isOpen = false;

export default ModalOverlay;
